import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { CheckoutService } from 'proto/gen/ts/shirt/v1/checkout_pb';
import { Link } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { ROUTES } from '../routes';
import { selectCartCount, selectCartTotal, useCartStore } from '../store/cartStore';
import { formatPrice } from '../utils/format';
import { transport } from '../utils/transport';
const checkoutClient = createClient(CheckoutService, transport);
export default function CartPage() {
    const items = useCartStore((s)=>s.items);
    const removeItem = useCartStore((s)=>s.removeItem);
    const updateQuantity = useCartStore((s)=>s.updateQuantity);
    const clearCart = useCartStore((s)=>s.clearCart);
    const rawTotalCents = useCartStore(selectCartTotal);
    const itemCount = useCartStore(selectCartCount);
    const { isWholesaler, isAuthenticated } = useCurrentUser();
    // Ask the server to re-price the cart for the current user. Only wholesalers will see
    // a different result; for retail customers, resolved == raw cart total.
    const shouldResolve = isAuthenticated && isWholesaler && items.length > 0;
    const { data: resolved } = useQuery({
        queryKey: [
            'resolveCartPrices',
            items.map((i)=>`${i.productId}:${i.quantity}`).join(',')
        ],
        queryFn: ()=>checkoutClient.resolveCartPrices({
                items: items.map((i)=>({
                        productId: i.productId,
                        quantity: i.quantity
                    }))
            }),
        enabled: shouldResolve
    });
    const resolvedLines = new Map(resolved?.lines.map((l)=>[
            l.productId,
            l
        ]) ?? []);
    const displayTotalCents = resolved ? Number(resolved.subtotalCents) : rawTotalCents;
    const showsWholesalePricing = !!resolved?.isWholesalePrice;
    if (items.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-display text-5xl text-stone-900 uppercase tracking-wide mb-4",
                    children: "Your Cart"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-stone-500 mb-8",
                    children: "Your cart is empty."
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-stone-700",
                    children: "Continue Shopping"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-10",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "w-10 h-1 bg-stone-900 mb-4"
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "font-display text-5xl text-stone-900 uppercase tracking-wide",
                        children: "Your Cart"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "mt-2 text-sm text-stone-500",
                        children: [
                            itemCount,
                            " ",
                            itemCount === 1 ? 'item' : 'items'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                children: items.map((item)=>{
                    const resolvedLine = resolvedLines.get(item.productId);
                    const resolvedUnit = resolvedLine ? Number(resolvedLine.unitPriceCents) : item.priceCents;
                    const discounted = resolvedUnit < item.priceCents;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4 sm:gap-6 py-6 border-b border-stone-100",
                        children: [
                            item.imageUrl ? /*#__PURE__*/ _jsx("img", {
                                src: item.imageUrl,
                                alt: item.name,
                                className: "w-20 h-24 sm:w-24 sm:h-28 object-cover flex-shrink-0 bg-stone-50"
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "w-20 h-24 sm:w-24 sm:h-28 bg-stone-50 flex-shrink-0"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-sm font-semibold text-stone-900 truncate",
                                        children: item.name
                                    }),
                                    discounted ? /*#__PURE__*/ _jsxs("p", {
                                        className: "mt-2 flex items-baseline gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-sm font-bold text-stone-900",
                                                children: formatPrice(resolvedUnit)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs text-stone-400 line-through",
                                                children: formatPrice(item.priceCents)
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5",
                                                children: "Wholesale"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-bold text-stone-900 mt-2",
                                        children: formatPrice(item.priceCents)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>updateQuantity(item.productId, item.quantity - 1),
                                        className: "w-9 h-9 border-2 border-stone-200 hover:border-stone-900 text-stone-600 hover:text-stone-900 font-bold",
                                        children: "−"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "w-10 text-center text-sm font-semibold text-stone-900",
                                        children: item.quantity
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>updateQuantity(item.productId, item.quantity + 1),
                                        className: "w-9 h-9 border-2 border-stone-200 hover:border-stone-900 text-stone-600 hover:text-stone-900 font-bold",
                                        children: "+"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-right w-20 hidden sm:block",
                                children: /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm font-bold text-stone-900",
                                    children: formatPrice(resolvedUnit * item.quantity)
                                })
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>removeItem(item.productId),
                                className: "p-2 text-stone-400 hover:text-stone-900",
                                "aria-label": "Remove",
                                children: /*#__PURE__*/ _jsxs("svg", {
                                    className: "w-4 h-4",
                                    fill: "none",
                                    viewBox: "0 0 24 24",
                                    stroke: "currentColor",
                                    "aria-hidden": "true",
                                    children: [
                                        /*#__PURE__*/ _jsx("title", {
                                            children: "Remove"
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            strokeLinecap: "round",
                                            strokeLinejoin: "round",
                                            strokeWidth: 2,
                                            d: "M6 18L18 6M6 6l12 12"
                                        })
                                    ]
                                })
                            })
                        ]
                    }, item.productId);
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-10 pt-8 border-t-2 border-stone-900",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between items-baseline mb-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm text-stone-500 uppercase tracking-wider",
                                children: "Subtotal"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-3xl font-display text-stone-900 uppercase",
                                children: formatPrice(displayTotalCents)
                            })
                        ]
                    }),
                    showsWholesalePricing && /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-green-700 mb-4",
                        children: "Wholesale pricing applied."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-stone-400 mb-6",
                        children: "Shipping calculated at checkout."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col sm:flex-row gap-3",
                        children: [
                            /*#__PURE__*/ _jsx(Link, {
                                to: ROUTES.checkout,
                                className: "flex-1 py-4 text-center bg-stone-900 text-white text-xs font-semibold uppercase tracking-[0.15em] hover:bg-stone-700",
                                children: "Proceed to Checkout"
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: ROUTES.shop,
                                className: "py-4 px-8 text-center border-2 border-stone-900 text-stone-900 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-stone-900 hover:text-white",
                                children: "Continue Shopping"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: clearCart,
                                className: "py-4 px-8 text-sm text-stone-400 hover:text-stone-900",
                                children: "Clear Cart"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
